import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    AdjustmentsHorizontalIcon,
    ArrowDownTrayIcon,
    BuildingStorefrontIcon,
    ChevronDownIcon,
    ClockIcon,
    Cog6ToothIcon,
    DocumentDuplicateIcon,
    DocumentTextIcon,
    MagnifyingGlassIcon,
    PencilSquareIcon,
    PlusIcon,
    TrashIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { LoaderSpinner } from "../../../components/LoaderSpinner";
import { OverlayGrayBackground } from "../../../components/OverlayGrayBackground";
import { ConfrimModal } from "../../../components/ConfrimModal";
import { VendorProductsSidebar } from "../../../components/VendorProductsSidebar";
import { ROUTES } from "../../../consts/general";
import { sortableVendorTableColumns } from "../../../consts/table";
import { arrayHasItems, getKeyByValue, getSortDirectionByTanStackTable } from "../../../helpers/common";
import { useDebounce } from "../../../helpers/useDebounce";
import { usePageReset } from "../../../helpers/usePageReset";
import {
    bulkAddTeamEmail,
    bulkAddVendorNote,
    bulkDeleteVendors,
    bulkUpdateVendorTerms,
    deleteVendor,
    getVendorsRequest,
    VendorsLoadingKeys,
} from "../../../slices/managementSlices/vendorsSlice";
import VendorBulkActionBar from "./VendorBulkActionBar";
import VendorColumnsDropdown from "./VendorColumnsDropdown";
import VendorAuditLogDrawer from "./VendorAuditLogDrawer";
import VendorDetailSheet from "./VendorDetailSheet";
import VendorSettingsModal from "./VendorSettingsModal";
import VendorTable from "./VendorTable";
import VendorCardList from "./VendorCardList";
import { exportVendorsToCsv } from "./vendorExport";
import { loadVendorColumnVisibility, saveVendorColumnVisibility } from "./vendorTableColumns";
import { getVendorCategories, parseVendorAccount, VENDOR_STATUSES } from "./vendorDisplay";
import { toast } from "react-toastify";

const DEFAULT_SORT = { id: "name", desc: false };

const SECONDARY_BUTTON_CLASS = "inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-white px-2.5 text-xs font-medium shadow-sm transition-colors hover:bg-gray-900 hover:text-white disabled:pointer-events-none disabled:opacity-50";
const FILTER_SELECT_CLASS = "h-9 appearance-none rounded-md border border-border bg-white pl-3 pr-9 text-sm shadow-sm outline-none focus:border-gray-400";

const FilterSelect = ({ value, onChange, options, allLabel, ariaLabel }) => (
    <div className="relative">
        <select
            aria-label={ariaLabel}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={FILTER_SELECT_CLASS}
        >
            <option value="">{allLabel}</option>
            {options.map((option) => (
                <option key={option} value={option}>{option}</option>
            ))}
        </select>
        <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
    </div>
);

const matchesVendorFilters = (vendor, { categoryFilter = "", statusFilter = "" } = {}) => {
    const { category, status } = parseVendorAccount(vendor);
    return (!categoryFilter || category === categoryFilter)
        && (!statusFilter || status === statusFilter);
};

const Vendors = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { vendors, isFirstLoadComplete, loading, loadingKeys } = useSelector((state) => state.vendors);

    const pageParams = window?.location?.search ? new URLSearchParams(window.location.search) : null;
    const [searchTerm, setSearchTerm] = useState(pageParams?.get("search") || "");
    const [currentPage, setCurrentPage] = useState(Number(pageParams?.get("page") || 1));
    const [currentItemsPerPage, setCurrentItemsPerPage] = useState(Number(pageParams?.get("itemsPerPage") || 100));
    const [selectedVendorId, setSelectedVendorId] = useState(pageParams?.get("currentVendor") || "");
    const [selectedSidebarVariant, setSelectedSidebarVariant] = useState(pageParams?.get("sidebarVariant") || "");
    const [sidebarVariant, setSidebarVariant] = useState(
        pageParams?.get("sidebarVariant") === "vendor" ? "edit" : null
    );
    const [isSidebarOpen, setIsSidebarOpen] = useState(pageParams?.get("sidebarVariant") === "vendor");
    const [isProductSidebarOpen, setIsProductSidebarOpen] = useState(pageParams?.get("sidebarVariant") === "products");
    const [selectedIds, setSelectedIds] = useState([]);
    const [vendorIdToDelete, setVendorIdToDelete] = useState("");
    const [deleteModalOpened, setDeleteModalOpened] = useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [isAuditLogOpen, setIsAuditLogOpen] = useState(false);
    const [columnVisibility, setColumnVisibility] = useState(() => loadVendorColumnVisibility());
    const [categoryFilter, setCategoryFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [draftCategoryFilter, setDraftCategoryFilter] = useState("");
    const [draftStatusFilter, setDraftStatusFilter] = useState("");
    const [isMobileFilterSheetOpen, setIsMobileFilterSheetOpen] = useState(false);
    const [mobileActionVendor, setMobileActionVendor] = useState(null);
    const [sorting, setSorting] = useState(() => {
        if (pageParams?.get("sortBy")) {
            return [{
                id: getKeyByValue(sortableVendorTableColumns, pageParams.get("sortBy")),
                desc: pageParams.get("sortDirection") === "DESC",
            }];
        }
        return [DEFAULT_SORT];
    });

    const debouncedSearch = useDebounce(searchTerm, 300);
    const shouldSkipRequest = usePageReset(setCurrentPage, currentPage, "get:/api/v1/management/vendors");

    const getSortParams = useCallback(() => {
        if (!arrayHasItems(sorting)) {
            return { sortBy: "", sortDirection: "" };
        }

        const sortValue = sorting[0];
        return {
            sortBy: sortableVendorTableColumns[sortValue.id] || "",
            sortDirection: getSortDirectionByTanStackTable(sortValue.desc),
        };
    }, [sorting]);

    const getRefreshParams = useCallback(() => ({
        search: debouncedSearch,
        page: currentPage,
        itemsPerPage: currentItemsPerPage,
        ...getSortParams(),
    }), [debouncedSearch, currentPage, currentItemsPerPage, getSortParams]);

    const updateRoute = () => {
        const { sortBy, sortDirection } = getSortParams();
        const params = new URLSearchParams({
            currentVendor: selectedVendorId,
            sidebarVariant: selectedSidebarVariant,
            search: debouncedSearch,
            page: currentPage,
            itemsPerPage: currentItemsPerPage,
            sortBy,
            sortDirection,
        });
        navigate(`${ROUTES.VENDORS}?${params}`);
    };

    const isLoading = loading && loadingKeys.includes(VendorsLoadingKeys.getVendors);
    const isDeleteVendorLoading = () => loadingKeys.includes(VendorsLoadingKeys.deleteVendor);
    const isBulkBusy = () => [
        VendorsLoadingKeys.bulkUpdateTerms,
        VendorsLoadingKeys.bulkAddNote,
        VendorsLoadingKeys.bulkAddTeamEmail,
        VendorsLoadingKeys.bulkDeleteVendors,
    ].some((key) => loadingKeys.includes(key));

    const selectedVendors = useMemo(
        () => vendors.filter((vendor) => selectedIds.includes(vendor.id)),
        [vendors, selectedIds]
    );

    const categories = useMemo(() => getVendorCategories(vendors), [vendors]);

    const filteredVendors = useMemo(
        () => vendors.filter((vendor) => matchesVendorFilters(vendor, { categoryFilter, statusFilter })),
        [vendors, categoryFilter, statusFilter]
    );
    const draftFilteredCount = useMemo(
        () => vendors.filter((vendor) => matchesVendorFilters(vendor, {
            categoryFilter: draftCategoryFilter,
            statusFilter: draftStatusFilter,
        })).length,
        [vendors, draftCategoryFilter, draftStatusFilter]
    );
    const activeFilterCount = Number(Boolean(categoryFilter)) + Number(Boolean(statusFilter));

    useEffect(() => {
        updateRoute();

        if (currentPage === 1 && shouldSkipRequest()) {
            return;
        }

        dispatch(getVendorsRequest(getRefreshParams()));
    }, [debouncedSearch, currentPage, currentItemsPerPage, sorting, dispatch, getRefreshParams, shouldSkipRequest]);

    useEffect(() => {
        updateRoute();
    }, [selectedVendorId, selectedSidebarVariant]);

    useEffect(() => {
        if (sidebarVariant === "create") {
            setSelectedVendorId("");
        }
    }, [sidebarVariant]);

    useEffect(() => {
        setSelectedIds((current) => current.filter((id) => vendors.some((vendor) => vendor.id === id)));
    }, [vendors]);

    const handleToggleSelect = useCallback((vendorId) => {
        setSelectedIds((current) => (
            current.includes(vendorId)
                ? current.filter((id) => id !== vendorId)
                : [...current, vendorId]
        ));
    }, []);

    const handleToggleSelectAll = useCallback(() => {
        setSelectedIds((current) => (
            current.length === filteredVendors.length ? [] : filteredVendors.map((vendor) => vendor.id)
        ));
    }, [filteredVendors]);

    const handleDeselectAll = useCallback(() => {
        setSelectedIds([]);
    }, []);

    const handleCopyVendorId = useCallback(async (vendorId) => {
        if (!vendorId) return;
        try {
            await navigator.clipboard.writeText(String(vendorId));
            toast.success('Vendor ID copied to clipboard', { position: 'bottom-right' });
        } catch (error) {
            console.error('Error copying vendor ID:', error);
            toast.error('Failed to copy vendor ID', { position: 'bottom-right' });
        }
    }, []);

    const handleApplyTerms = useCallback(async (terms) => {
        await dispatch(bulkUpdateVendorTerms({
            vendorIds: selectedIds,
            terms,
            refreshParams: getRefreshParams(),
        }));
        setSelectedIds([]);
    }, [dispatch, getRefreshParams, selectedIds]);

    const handleAddNote = useCallback(async (note) => {
        await dispatch(bulkAddVendorNote({
            vendorIds: selectedIds,
            note,
            refreshParams: getRefreshParams(),
        }));
        setSelectedIds([]);
    }, [dispatch, getRefreshParams, selectedIds]);

    const handleAddTeamEmail = useCallback(async (email) => {
        await dispatch(bulkAddTeamEmail({
            vendorIds: selectedIds,
            email,
            refreshParams: getRefreshParams(),
        }));
        setSelectedIds([]);
    }, [dispatch, getRefreshParams, selectedIds]);

    const handleExport = useCallback(() => {
        exportVendorsToCsv(selectedVendors);
    }, [selectedVendors]);

    const handleExportAll = useCallback(() => {
        exportVendorsToCsv(selectedVendors.length ? selectedVendors : filteredVendors);
    }, [selectedVendors, filteredVendors]);

    const handleBulkDelete = useCallback(async () => {
        await dispatch(bulkDeleteVendors({
            vendorIds: selectedIds,
            refreshParams: getRefreshParams(),
        }));
        setSelectedIds([]);
    }, [dispatch, getRefreshParams, selectedIds]);

    const handleCreateVendor = useCallback(() => {
        setSelectedVendorId("");
        setSelectedSidebarVariant("");
        setSidebarVariant("create");
        setIsSidebarOpen(true);
        setIsProductSidebarOpen(false);
    }, []);

    const handleDeleteVendor = useCallback((id) => {
        setVendorIdToDelete(id);
        setDeleteModalOpened(true);
    }, []);

    const openMobileFilterSheet = useCallback(() => {
        setDraftCategoryFilter(categoryFilter);
        setDraftStatusFilter(statusFilter);
        setIsMobileFilterSheetOpen(true);
    }, [categoryFilter, statusFilter]);

    const openEditSidebar = useCallback((vendor) => {
        setSelectedVendorId(vendor.id);
        setSelectedSidebarVariant("vendor");
        setSidebarVariant("edit");
        setIsSidebarOpen(true);
        setIsProductSidebarOpen(false);
    }, []);

    const openProductsSidebar = useCallback((vendor) => {
        setSelectedVendorId(vendor.id);
        setSelectedSidebarVariant("products");
        setIsProductSidebarOpen(true);
        setIsSidebarOpen(false);
        setSidebarVariant(null);
    }, []);

    const closeProductsSidebar = useCallback(() => {
        setSelectedVendorId("");
        setSelectedSidebarVariant("");
        setIsProductSidebarOpen(false);
    }, []);

    const closeSidebar = useCallback(() => {
        setSelectedVendorId("");
        setSidebarVariant(null);
        setSelectedSidebarVariant("");
        setIsSidebarOpen(false);
    }, []);

    const handleMobileAction = useCallback((action) => {
        const vendor = mobileActionVendor;
        setMobileActionVendor(null);
        if (!vendor) return;

        if (action === "copy") {
            handleCopyVendorId(vendor.vendorId);
        } else if (action === "products") {
            openProductsSidebar(vendor);
        } else if (action === "edit") {
            openEditSidebar(vendor);
        } else if (action === "delete") {
            handleDeleteVendor(vendor.id);
        }
    }, [handleCopyVendorId, handleDeleteVendor, mobileActionVendor, openEditSidebar, openProductsSidebar]);

    const handleVendorSaved = useCallback(() => {
        closeSidebar();
        dispatch(getVendorsRequest(getRefreshParams()));
    }, [closeSidebar, dispatch, getRefreshParams]);

    const defaultEmails = vendors[0]?.defaultEmails || [];

    const handleColumnVisibilityChange = useCallback((nextVisibility) => {
        setColumnVisibility(nextVisibility);
        saveVendorColumnVisibility(nextVisibility);
    }, []);

    const handleSort = useCallback((columnId) => {
        setSorting((current) => {
            const currentSort = current[0];
            if (currentSort?.id === columnId) {
                return [{ id: columnId, desc: !currentSort.desc }];
            }
            return [{ id: columnId, desc: false }];
        });
    }, []);

    return (
        <main className="box-border flex min-h-screen w-full flex-col overflow-x-clip md:h-screen md:min-h-0 md:overflow-hidden md:p-6">
            <div className="contents">
                <div className="sticky top-14 z-40 border-b border-gray-200 bg-white px-4 pb-2.5 pt-3 md:hidden">
                    <div className="flex h-9 items-center gap-2">
                        <h1 className="mr-auto min-w-0 text-xl font-bold text-gray-900">Suppliers</h1>
                        <button
                            type="button"
                            aria-label="Change History"
                            title="Change History"
                            onClick={() => setIsAuditLogOpen(true)}
                            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-white text-gray-900 shadow-sm"
                        >
                            <ClockIcon className="h-5 w-5" />
                        </button>
                        <button
                            type="button"
                            aria-label="Export"
                            title="Export"
                            onClick={handleExportAll}
                            disabled={!filteredVendors.length}
                            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-white text-gray-900 shadow-sm disabled:pointer-events-none disabled:opacity-50"
                        >
                            <ArrowDownTrayIcon className="h-5 w-5" />
                        </button>
                        <button
                            type="button"
                            aria-label="Settings"
                            title="Settings"
                            onClick={() => setIsSettingsModalOpen(true)}
                            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-white text-gray-900 shadow-sm"
                        >
                            <Cog6ToothIcon className="h-5 w-5" />
                        </button>
                        <button
                            type="button"
                            onClick={handleCreateVendor}
                            className="inline-flex h-9 shrink-0 items-center gap-1 rounded-md bg-gray-900 px-2.5 text-sm font-semibold text-white"
                        >
                            <PlusIcon className="h-4 w-4" strokeWidth={2} />
                            New
                        </button>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                        <div className="relative min-w-0 flex-1">
                            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <input
                                aria-label="Search vendors"
                                placeholder="Search vendors..."
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                className="h-10 w-full rounded-md border border-border bg-white pl-9 pr-9 text-[16px] shadow-sm outline-none focus:border-gray-400 placeholder:text-gray-500"
                            />
                            {searchTerm && (
                                <button
                                    type="button"
                                    aria-label="Clear search"
                                    onClick={() => setSearchTerm("")}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-500 hover:bg-gray-100"
                                >
                                    <XMarkIcon className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                        <button
                            type="button"
                            aria-label="Filters"
                            title="Filters"
                            onClick={openMobileFilterSheet}
                            className="relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-white text-gray-900 shadow-sm"
                        >
                            <AdjustmentsHorizontalIcon className="h-5 w-5" />
                            {activeFilterCount > 0 && (
                                <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-gray-900 px-1 text-[10px] font-semibold leading-none text-white">
                                    {activeFilterCount}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex flex-col overflow-x-hidden md:min-h-0 md:flex-1 md:overflow-visible">
                <div className="hidden shrink-0 flex-col gap-4 md:flex md:flex-row md:items-start md:justify-between">
                    <div className="min-w-0">
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-semibold text-gray-900">Suppliers</h1>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    aria-current="page"
                                    className="inline-flex h-8 items-center gap-1.5 rounded-md bg-gray-900 px-3 text-xs font-medium text-white shadow-sm"
                                >
                                    <BuildingStorefrontIcon className="h-4 w-4" />
                                    Vendors
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsSettingsModalOpen(true)}
                                    className="inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-medium text-gray-900 hover:bg-gray-200 transition-colors"
                                >
                                    <Cog6ToothIcon className="h-4 w-4" />
                                    Settings
                                </button>
                            </div>
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">
                            Manage supplier contacts, account numbers and payment / net terms
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setIsAuditLogOpen(true)}
                            className={SECONDARY_BUTTON_CLASS}
                        >
                            <ClockIcon className="h-4 w-4" />
                            Change History
                        </button>
                        <button
                            type="button"
                            onClick={handleExportAll}
                            disabled={!filteredVendors.length}
                            className={SECONDARY_BUTTON_CLASS}
                        >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            Export
                        </button>
                        <button
                            type="button"
                            onClick={handleCreateVendor}
                            className="inline-flex h-8 items-center gap-1 whitespace-nowrap rounded-md bg-gray-900 px-2.5 text-xs font-medium text-white shadow-sm hover:bg-gray-800 transition-colors"
                        >
                            <PlusIcon className="h-4 w-4" strokeWidth={2} />
                            Create Vendor
                        </button>
                    </div>
                </div>

                <div className="hidden shrink-0 mt-6 flex-wrap items-center gap-3 md:flex">
                    <div className="relative w-full md:w-[380px]">
                        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <input
                            placeholder="Search vendors..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="h-9 w-full rounded-md border border-border bg-white pl-10 pr-3 text-sm shadow-sm outline-none focus:border-gray-400 placeholder:text-gray-500"
                        />
                    </div>
                    <FilterSelect
                        ariaLabel="Filter by category"
                        value={categoryFilter}
                        onChange={setCategoryFilter}
                        options={categories}
                        allLabel="All Categories"
                    />
                    <FilterSelect
                        ariaLabel="Filter by status"
                        value={statusFilter}
                        onChange={setStatusFilter}
                        options={VENDOR_STATUSES}
                        allLabel="All Status"
                    />
                    <VendorColumnsDropdown
                        visibleColumns={columnVisibility}
                        onChange={handleColumnVisibilityChange}
                    />
                    <span className="ml-auto text-sm text-muted-foreground">
                        {filteredVendors.length} {filteredVendors.length === 1 ? "vendor" : "vendors"}
                    </span>
                </div>
                <div className="px-4 py-3 md:hidden">
                    <div className="flex items-center justify-between gap-3 text-sm text-gray-500">
                        <span>{filteredVendors.length} {filteredVendors.length === 1 ? "vendor" : "vendors"}</span>
                        <button
                            type="button"
                            onClick={() => handleSort("name")}
                            className="inline-flex items-center gap-1.5 text-gray-500"
                        >
                            {sorting[0]?.desc ? "Name Z–A" : "Name A–Z"}
                            <ChevronDownIcon className="h-4 w-4" />
                        </button>
                    </div>
                </div>
                <div className="hidden shrink-0 md:block">
                    <VendorBulkActionBar
                        selectedCount={selectedIds.length}
                        busy={isBulkBusy()}
                        onDeselectAll={handleDeselectAll}
                        onApplyTerms={handleApplyTerms}
                        onAddNote={handleAddNote}
                        onAddTeamEmail={handleAddTeamEmail}
                        onExport={handleExport}
                        onBulkDelete={handleBulkDelete}
                    />
                </div>
                <div className="relative mt-4 hidden flex-1 min-h-0 overflow-auto rounded-lg border border-border md:block">
                    {isFirstLoadComplete && isLoading && <OverlayGrayBackground />}

                    {!isFirstLoadComplete && isLoading ? (
                        <div className="h-60">
                            <LoaderSpinner width="70" height="70" border="3" colorSpinner="black" />
                        </div>
                    ) : (
                        <VendorTable
                            vendors={filteredVendors}
                            selectedIds={selectedIds}
                            visibleColumns={columnVisibility}
                            sorting={sorting[0]}
                            onSort={handleSort}
                            onToggleSelect={handleToggleSelect}
                            onToggleSelectAll={handleToggleSelectAll}
                            onDeleteVendor={handleDeleteVendor}
                            onCopyVendorId={handleCopyVendorId}
                            onEditVendor={openEditSidebar}
                            onViewProducts={openProductsSidebar}
                            onRowClick={openEditSidebar}
                        />
                    )}
                </div>
                <VendorCardList
                    vendors={filteredVendors}
                    isFirstLoadComplete={isFirstLoadComplete}
                    isLoading={isLoading}
                    onRowClick={openEditSidebar}
                    onOpenActions={setMobileActionVendor}
                />
            </div>

            {isMobileFilterSheetOpen && (
                <div className="fixed inset-0 z-[150] md:hidden">
                    <div
                        aria-hidden="true"
                        className="absolute inset-0 bg-gray-900/40"
                        onClick={() => setIsMobileFilterSheetOpen(false)}
                    />
                    <section
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="vendor-mobile-filters-title"
                        className="absolute inset-x-0 bottom-0 z-10 flex max-h-[88vh] flex-col rounded-t-2xl bg-white pb-4 shadow-xl"
                    >
                        <div className="mx-auto mt-2 h-1.5 w-9 rounded-full bg-gray-300" />
                        <div className="flex shrink-0 items-center justify-between px-4 pb-3 pt-2">
                            <h2 id="vendor-mobile-filters-title" className="text-base font-bold text-gray-900">Filters</h2>
                            <button
                                type="button"
                                onClick={() => {
                                    setDraftCategoryFilter("");
                                    setDraftStatusFilter("");
                                }}
                                className="text-sm text-gray-500"
                            >
                                Reset
                            </button>
                        </div>
                        <div className="overflow-y-auto px-4 pb-4">
                            <div className="mb-2 mt-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Status</div>
                            <div className="flex flex-wrap gap-2">
                                {["", ...VENDOR_STATUSES].map((status) => {
                                    const isSelected = draftStatusFilter === status;
                                    return (
                                        <button
                                            key={status || "all-statuses"}
                                            type="button"
                                            aria-pressed={isSelected}
                                            onClick={() => setDraftStatusFilter(status)}
                                            className={`h-[34px] rounded-full border px-3 text-sm ${isSelected ? "border-gray-900 bg-gray-900 text-white" : "border-border bg-white text-gray-900"}`}
                                        >
                                            {status || "All"}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="mb-2 mt-4 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Category</div>
                            <div className="flex flex-wrap gap-2">
                                {["", ...categories].map((category) => {
                                    const isSelected = draftCategoryFilter === category;
                                    return (
                                        <button
                                            key={category || "all-categories"}
                                            type="button"
                                            aria-pressed={isSelected}
                                            onClick={() => setDraftCategoryFilter(category)}
                                            className={`min-h-[34px] rounded-full border px-3 py-1 text-sm ${isSelected ? "border-gray-900 bg-gray-900 text-white" : "border-border bg-white text-gray-900"}`}
                                        >
                                            {category || "All"}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="shrink-0 border-t border-border px-4 pt-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setCategoryFilter(draftCategoryFilter);
                                    setStatusFilter(draftStatusFilter);
                                    setIsMobileFilterSheetOpen(false);
                                }}
                                className="h-11 w-full rounded-lg bg-gray-900 text-[15px] font-semibold text-white"
                            >
                                Show {draftFilteredCount} {draftFilteredCount === 1 ? "vendor" : "vendors"}
                            </button>
                        </div>
                    </section>
                </div>
            )}

            {mobileActionVendor && (
                <div className="fixed inset-0 z-[150] md:hidden">
                    <div
                        aria-hidden="true"
                        className="absolute inset-0 bg-gray-900/40"
                        onClick={() => setMobileActionVendor(null)}
                    />
                    <section
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="vendor-mobile-actions-title"
                        className="absolute inset-x-0 bottom-0 z-10 flex max-h-[88vh] flex-col rounded-t-2xl bg-white pb-4 shadow-xl"
                    >
                        <div className="mx-auto mt-2 h-1.5 w-9 rounded-full bg-gray-300" />
                        <div className="px-4 pb-3 pt-3">
                            <h2 id="vendor-mobile-actions-title" className="truncate text-base font-bold text-gray-900">
                                {mobileActionVendor.name || "Vendor"}
                            </h2>
                            <p className="mt-1 text-sm text-gray-500">
                                Vendor ID {mobileActionVendor.vendorId || "—"}
                            </p>
                        </div>
                        <div className="px-4">
                            <button
                                type="button"
                                onClick={() => handleMobileAction("copy")}
                                className="flex h-12 w-full items-center gap-3 border-b border-gray-100 text-left text-[15px] text-gray-900"
                            >
                                <DocumentDuplicateIcon className="h-5 w-5" />
                                Copy vendor ID
                            </button>
                            <button
                                type="button"
                                onClick={() => handleMobileAction("products")}
                                className="flex h-12 w-full items-center gap-3 border-b border-gray-100 text-left text-[15px] text-gray-900"
                            >
                                <DocumentTextIcon className="h-5 w-5" />
                                View products
                            </button>
                            <button
                                type="button"
                                onClick={() => handleMobileAction("edit")}
                                className="flex h-12 w-full items-center gap-3 border-b border-gray-100 text-left text-[15px] text-gray-900"
                            >
                                <PencilSquareIcon className="h-5 w-5" />
                                Edit vendor
                            </button>
                            <button
                                type="button"
                                onClick={() => handleMobileAction("delete")}
                                className="flex h-12 w-full items-center gap-3 text-left text-[15px] text-red-600"
                            >
                                <TrashIcon className="h-5 w-5" />
                                Delete vendor
                            </button>
                        </div>
                        <div className="mt-2 shrink-0 border-t border-border px-4 pt-3">
                            <button
                                type="button"
                                onClick={() => setMobileActionVendor(null)}
                                className="h-11 w-full rounded-lg border border-border text-[15px] font-semibold text-gray-900"
                            >
                                Cancel
                            </button>
                        </div>
                    </section>
                </div>
            )}

            <VendorDetailSheet
                vendorId={selectedVendorId}
                variant={sidebarVariant}
                isOpen={isSidebarOpen}
                onClose={closeSidebar}
                onSuccessSubmit={handleVendorSaved}
                defaultEmails={defaultEmails}
            />

            <VendorProductsSidebar
                vendorId={selectedVendorId}
                isOpen={isProductSidebarOpen}
                onClose={closeProductsSidebar}
            />

            <VendorAuditLogDrawer
                isOpen={isAuditLogOpen}
                onClose={() => setIsAuditLogOpen(false)}
            />

            <VendorSettingsModal
                isOpen={isSettingsModalOpen}
                onClose={() => setIsSettingsModalOpen(false)}
                onSuccess={() => dispatch(getVendorsRequest(getRefreshParams()))}
            />

            <ConfrimModal
                isOpen={deleteModalOpened}
                title="Are you sure want to delete this vendor?"
                confirmButtonTitle="Delete"
                isLoading={isDeleteVendorLoading()}
                submitClick={async () => {
                    const idToDelete = vendorIdToDelete;
                    setDeleteModalOpened(false);
                    await dispatch(deleteVendor({ id: idToDelete, search: debouncedSearch }));
                    setVendorIdToDelete("");
                    setSelectedIds((current) => current.filter((id) => id !== idToDelete));
                }}
                cancelClick={() => {
                    setDeleteModalOpened(false);
                    setVendorIdToDelete("");
                }}
            />
        </main>
    );
};

export default Vendors;
